import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import type { Event, EventProps, EventWrapperProps } from 'react-big-calendar';

import { FREQUENCY_MODE } from 'app/typings/core/extranet/inventory/enums/FrequencyMode';

import { getPopoverKey, getPopoverPlacement } from '../../utils/events';
import type { AvailabilityFormValues } from '../AvailabilityForm/availabilityForm.types';
import { AvailabilityPopover } from '../AvailabilityPopover/AvailabilityPopover';
import { CalendarEvent } from '../CalendarEvent/CalendarEvent';
import type { TimeslotEventResource } from '../CalendarEvent/CalendarEvent';

const ContinuesPriorContext = createContext(false);

export const EventSegmentWrapper = ({
  continuesPrior,
  children,
}: EventWrapperProps & {
  continuesPrior?: boolean;
  children?: ReactNode;
}) => (
  <ContinuesPriorContext.Provider value={!!continuesPrior}>
    {children}
  </ContinuesPriorContext.Provider>
);

type Props = EventProps & {
  activityName: string;
  durationMinutes?: number;
  onPreview: (event: Event, values: AvailabilityFormValues | null) => void;
  onSave: (event: Event, values: AvailabilityFormValues) => void;
  onDelete: (event: Event) => void;
  isWithinOpeningHours?: (start: Dayjs) => boolean;
  isDayClosed?: (date: Dayjs) => boolean;
};

export const EventPopover = ({
  activityName,
  durationMinutes,
  onPreview,
  onSave,
  onDelete,
  isWithinOpeningHours,
  isDayClosed,
  ...eventProps
}: Props) => {
  const { event } = eventProps;
  const continuesPrior = useContext(ContinuesPriorContext);
  const resource = event.resource as TimeslotEventResource | undefined;

  if (resource?.isTemporary) {
    return <CalendarEvent {...eventProps} selected />;
  }

  const frequency =
    resource?.frequency ??
    (resource?.isRecurrent ? FREQUENCY_MODE.WEEKLY : FREQUENCY_MODE.NONE);

  return (
    <AvailabilityPopover
      id={`${getPopoverKey(event)}${continuesPrior ? '-next-day' : ''}`}
      title="Edit availabilities"
      subtitle={activityName}
      placement={getPopoverPlacement(event.start)}
      initialValues={{
        startTime: dayjs(event.start),
        endTime: dayjs(event.end),
        date: dayjs(event.start),
        capacity:
          (resource?.totalBooked ?? 0) + (resource?.slotsLeft ?? 0) || 1,
        frequency,
        weekdays: resource?.weekdays,
      }}
      durationMinutes={durationMinutes}
      onChange={(values) => onPreview(event, values)}
      onSubmit={(values) => onSave(event, values)}
      onDelete={() => onDelete(event)}
      isWithinOpeningHours={isWithinOpeningHours}
      isDayClosed={isDayClosed}
      isEditPopover={true}
    >
      {({ open }) => <CalendarEvent {...eventProps} selected={open} />}
    </AvailabilityPopover>
  );
};
